import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Chip,
  Tooltip,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  setSelectedReportTag,
  setSavedSelectedReportTagData,
  setSelectedReportStatus,
  setSavedSelectedReportStatusData,
} from "../../actions";
import _ from "lodash";
import TagConfig from "../../config/TagConfig";
import LocalStorageHelper from "../../utils/helper/localstorage_helper";

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      marginBottom: theme.spacing(2),
      maxWidth: 1200, // Magnify に合わせた最大幅
      width: "100%", // 横幅を100%に統一
      marginLeft: "auto",
      marginRight: "auto",
    },
    title: {
      fontSize: 12,
      color: "#484848",
    },
    item: {
      marginTop: theme.spacing(1),
    },
    filtered: {
      flex: 1,
      maxWidth: 1200,
      width: "100%", // 横幅を100%に統一
      position: "relative",
    },
    iconTooltip: {
      fontSize: 18,
      marginTop: -4,
      marginLeft: 4,
    },
    tagSelect: {
      marginTop: theme.spacing(1),
      fontSize: 16,
      minWidth: 400,
      maxWidth: 600,
      "& .MuiInputLabel-root": {
        fontSize: "14px",
      },
      "& .MuiInputBase-input": {
        fontSize: "14px",
      },
    },
    tagWrapper: {
      display: "flex",
    },
    tagDropdown: {
      marginLeft: -32,
    },
    saveButton: {
      fontWeight: "medium",
      height: 30,
      marginTop: 12,
      marginLeft: 16,
      paddingTop: 8,
    },
    formWrapper: {
      display: "flex",
    },
    form: {
      marginRight: 16,
      width: "85.9%",
      "& .MuiTextField-root": {
        margin: theme.spacing(1),
      },
    },
  }),
);

const ReportMeta = ({
  viewer,
  setSelectedReportTag,
  setSelectedReportStatus,
  selectedReportTag,
  selectedReportStatus,
  setSavedSelectedReportTagData,
  setSavedSelectedReportStatusData,
  tagSavingLoader,
  reportStatusSavingLoader,
}) => {
  const classes = useStyles();
  const [tagData, setTagData] = useState(
    TagConfig.reportResultTagTemp2.slice(1),
  );
  const [birdNestReportOption, setBirdNestReportOption] = useState(
    TagConfig.tepcoBirdNestStatusOption,
  );

  const [photoData, setPhotoData] = useState([]);
  const [flagRemovedChecked, setFlagRemovedChecked] = useState(false);
  const companyName = LocalStorageHelper.getCompany();
  const savedBy = LocalStorageHelper.getLoggedinUserEmail();

  useEffect(() => {
    if (viewer.length > 0 && viewer[0].allPhotoUrl) {
      const uniquePhotoData = _.uniqWith(viewer[0].allPhotoUrl, _.isEqual);
      setPhotoData(uniquePhotoData);
    }
  }, [viewer]);

  useEffect(() => {
    if (viewer.length === 0) return;
    if (!selectedReportStatus.report_status) return;
    if (selectedReportStatus.report_id !== viewer[0].reportId) return;
    if (selectedReportStatus.file_name) return;

    const statusData = {
      ...selectedReportStatus,
      report_id: viewer[0].reportId,
      file_name: viewer[0].fileName,
      file_url: viewer[0].fileUrl,
      occurred_datetime: viewer[0].occurred_datetime_jst,
      game_space_id: viewer[0].gameSpaceId,
      power_asset_id: viewer[0].powerAssetId,
      place_code: viewer[0].placeCode,
      sign_number: viewer[0].signNumber,
      sign_name: viewer[0].signName,
      equipment_number: viewer[0].equipmentNumber,
      equipment_code: viewer[0].equipmentCode,
      player_id: viewer[0].playerId,
      player_name: viewer[0].user,
      tags: viewer[0].tag,
      tag_display: viewer[0].tagDisplay,
      company: selectedReportStatus.company || companyName,
    };
    setSelectedReportStatus(statusData);
  }, [viewer, selectedReportStatus, companyName, setSelectedReportStatus]);

  useEffect(() => {
    if (!selectedReportStatus.report_status) {
      setFlagRemovedChecked(false);
      return;
    }
    setFlagRemovedChecked(!!selectedReportStatus.flag_removed);
  }, [selectedReportStatus.report_status, selectedReportStatus.flag_removed]);

  const handleSelectReportTagChange = (tags) => {
    const savingTagData = {
      report_id: viewer[0].reportId,
      file_name: viewer[0].fileName,
      file_url: viewer[0].fileUrl,
      occurred_datetime: viewer[0].occurred_datetime_jst,
      game_space_id: viewer[0].gameSpaceId,
      power_asset_id: viewer[0].powerAssetId,
      place_code: viewer[0].placeCode,
      sign_number: viewer[0].signNumber,
      equipment_number: viewer[0].equipmentNumber,
      equipment_code: viewer[0].equipmentCode,
      player_id: viewer[0].playerId,
      player_name: viewer[0].user,
      tags: viewer[0].tag,
      result_tags: tags,
    };
    setSelectedReportTag(savingTagData);
  };

  const handleSelectReportStatusChange = (status) => {
    if (!status || !status.value) {
      setSelectedReportStatus({});
      setFlagRemovedChecked(false);
      return;
    }

    const savingStatusData = {
      report_id: viewer[0].reportId,
      file_name: viewer[0].fileName,
      file_url: viewer[0].fileUrl,
      occurred_datetime: viewer[0].occurred_datetime_jst,
      game_space_id: viewer[0].gameSpaceId,
      power_asset_id: viewer[0].powerAssetId,
      place_code: viewer[0].placeCode,
      sign_number: viewer[0].signNumber,
      sign_name: viewer[0].signName,
      equipment_number: viewer[0].equipmentNumber,
      equipment_code: viewer[0].equipmentCode,
      player_id: viewer[0].playerId,
      player_name: viewer[0].user,
      tags: viewer[0].tag,
      tag_display: viewer[0].tagDisplay,
      report_status: status.value,
      report_status_label: status.label || status.value,
      flag_removed: flagRemovedChecked,
      flag_reset: false, // @todo: configure this once the data source is identified for reset status
      report_status_saved_by: savedBy,
      company: companyName,
    };
    setSelectedReportStatus(savingStatusData);
  };

  return (
    <div className={classes.container}>
      <Grid
        container
        direction="row"
        alignItems="flex-start"
        className={classes.filtered}
      >
        {viewer.length > 0 && (
          <>
            <Grid item className={classes.item} xs={4} md={2}>
              <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                {companyName === "tepco" && `標識番号 | 標識名：`}
                {(companyName.includes("ntt") || companyName === "okiden") &&
                  `県域 | 収容局名 :`}
                {companyName === "hokuden" && `電柱番号 :`}
              </Typography>
              {viewer[0]["signNumber"] !== undefined &&
                companyName === "tepco" &&
                `${viewer[0]["signNumber"]} | ${viewer[0]["signName"]}`}
              {viewer[0]["prefecturalArea"] !== undefined &&
                (companyName.includes("ntt") || companyName === "okiden") &&
                `${viewer[0]["prefecturalArea"]} | ${viewer[0]["exchangeOfficeName"]}`}
              {viewer[0]["powerPoleNumber"] !== undefined &&
                companyName === "hokuden" &&
                `${viewer[0]["powerPoleNumber"]}`}
            </Grid>
            <Grid item className={classes.item} xs={4} md={2}>
              <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                {companyName === "tepco" && `設備番号 | スキーマ番号：`}
                {(companyName.includes("ntt") || companyName === "okiden") &&
                  `電柱情報 :`}
                {companyName === "hokuden" && `市町村名 :`}
              </Typography>
              {viewer[0]["equipmentNumber"] !== undefined &&
                companyName === "tepco" &&
                `${viewer[0]["equipmentNumber"]} | ${viewer[0]["placeCode"]}`}
              {viewer[0]["assetInfo"] !== undefined &&
                (companyName.includes("ntt") || companyName === "okiden") &&
                `${viewer[0]["assetInfo"]}`}
              {viewer[0]["representativeMunicipalityName"] !== undefined &&
                companyName === "hokuden" &&
                `${viewer[0]["representativeMunicipalityName"]}`}
            </Grid>
            {(companyName.includes("ntt") || companyName === "okiden") && (
              <Grid item className={classes.item} xs={4} md={2}>
                <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                  撮影対象：
                </Typography>
                {viewer[0]["photographyTarget"] !== undefined
                  ? `${viewer[0]["photographyTarget"]}`
                  : "-"}
              </Grid>
            )}
            {companyName === "hokuden" && (
              <Grid item className={classes.item} xs={4} md={2}>
                <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                  支店コード：
                </Typography>
                {viewer[0]["branchCode"] !== undefined
                  ? `${viewer[0]["branchCode"]}`
                  : "-"}
              </Grid>
            )}
            <Grid item className={classes.item} xs={4} md={2}>
              <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                撮影点数：
              </Typography>
              {!viewer[0]["allPhotoUrl"][0] ? "0" : photoData.length}
            </Grid>
            <Grid item className={classes.item} xs={4} md={2}>
              <Typography color="textSecondary" style={{ marginLeft: 0 }}>報告者 (Player ID)：</Typography>
              {viewer[0].user} <br /><span style={{fontSize: 12}}>{viewer[0].playerId}</span>
            </Grid>
            <Grid item className={classes.item} xs={4} md={2}>
              <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                報告日時：
              </Typography>
              {viewer[0].occurred_datetime_jst
                ? viewer[0].occurred_datetime_jst.slice(0, 19).replace("T", " ")
                : "-"}
            </Grid>
            {companyName === "tepco" && (
              <Grid item className={classes.item} xs={4} md={2}>
                <Typography color="textSecondary" style={{ marginLeft: 0 }}>
                  高圧設備：
                </Typography>
                {viewer[0]["withInsulatedSwitch"] ? `有` : "無"}
              </Grid>
            )}
            <Grid
              item
              className={classes.item}
              xs={12}
              spacing={3}
              style={{ marginTop: 16 }}
            >
              <Grid container direction="row" alignItems="flex-start">
                <Grid item lg={6} md={6} xs={12}>
                  <Typography
                    color="textSecondary"
                    style={{ marginLeft: 0, marginBottom: 4 }}
                  >
                    報告タグ：
                  </Typography>
                  {viewer[0].tag.length > 0
                    ? viewer[0].tag.map((tag, index) => (
                        <Tooltip
                          title={
                            viewer[0]["tagDisplay"][index] !== "その他"
                              ? viewer[0]["tagDisplay"][index]
                              : ""
                          }
                          placement="top"
                        >
                          <Chip
                            key={index}
                            style={{
                              backgroundColor: "#ffed6f",
                              color: "black",
                              paddingTop: "0px !important",
                            }}
                            size="small"
                            label={tag}
                          ></Chip>
                        </Tooltip>
                      ))
                    : "-"}
                </Grid>
                <Grid item lg={6} md={6} xs={12}>
                  <Typography
                    color="textSecondary"
                    style={{ marginLeft: 0, marginBottom: 4 }}
                  >
                    報告内容：
                  </Typography>
                  {viewer[0].comment ? viewer[0].comment : "-"}
                </Grid>
              </Grid>
            </Grid>
            {viewer[0]["tag"].some((tag) => tag.includes("ツルツタ")) && (
              <Grid item className={classes.item} xs={12}>
                <Grid item className={classes.item} xs={12}>
                  <Grid
                    container
                    direction="row"
                    alignItems="flex-start"
                    className={classes.filtered}
                  >
                    <Grid item className={classes.item} xs={12} md={6} lg={2}>
                      <div
                        style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 14 }}
                      >
                        報告確認結果入力
                      </div>
                    </Grid>
                    <Grid
                      item
                      className={classes.tagDropdown}
                      xs={12}
                      md={12}
                      lg={8}
                    >
                      {tagData !== null && tagData.length > 0 && (
                        <div className={classes.tagWrapper}>
                          <Autocomplete
                            multiple
                            id="tag-select-autocomplete"
                            options={tagData}
                            getOptionLabel={(option) => option.value || ""}
                            getOptionSelected={(option, value) =>
                              option.value.toString() === value.toString()
                            }
                            value={
                              selectedReportTag.result_tags !== undefined &&
                              selectedReportTag.result_tags.length > 0
                                ? tagData.filter((tag) =>
                                    selectedReportTag.result_tags.includes(
                                      tag.value.toString(),
                                    ),
                                  )
                                : []
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                const selectedValues = newValue.map((tag) =>
                                  tag.value.toString(),
                                );
                                console.log(
                                  "LOG: selectedValues:",
                                  selectedValues,
                                );
                                handleSelectReportTagChange(selectedValues);
                              } else {
                                handleSelectReportTagChange([]);
                              }
                            }}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  key={index}
                                  size="small"
                                  label={`${option.label} : ${option.tip}`}
                                  {...getTagProps({ index })}
                                  style={{
                                    backgroundColor: "#ffed6f",
                                    color: "black",
                                  }} // Set Chip color
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder={
                                  selectedReportTag.tags !== undefined &&
                                  selectedReportTag.tags.length < 1
                                    ? "選択する"
                                    : ""
                                }
                                className={classes.tagSelect}
                              />
                            )}
                            renderOption={(option, { selected }) => {
                              const isFirstOldItem =
                                option.value === "ツルツタLv.0−1";
                              return (
                                <Box
                                  width="100%"
                                  display="flex"
                                  flexDirection="column"
                                >
                                  {isFirstOldItem && (
                                    <Divider
                                      style={{
                                        margin: "8px 0",
                                        borderTop: "2px solid #aaa",
                                        width: "100%",
                                      }}
                                    />
                                  )}
                                  <Box
                                    pl={option.indent ? 3 : 0}
                                  >
                                    <Typography variant="body1">
                                      <b>{option.label}</b> : {option.tip}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            color="default"
                            className={classes.saveButton}
                            onClick={setSavedSelectedReportTagData}
                          >
                            {tagSavingLoader ? "保存中.." : "保存"}
                          </Button>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {companyName === "tepco" && viewer[0]["tag"].some((tag) => tag.includes("ツルツタ1")) &&
            birdNestReportOption !== null && birdNestReportOption.length > 0 && (
              <Grid item className={classes.item} xs={12}>
                <Grid item className={classes.item} xs={12}>
                  <Grid
                    container
                    direction="row"
                    alignItems="flex-start"
                    className={classes.filtered}
                  >
                    <Grid item className={classes.item} xs={12} md={6} lg={2}>
                      <div
                        style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 14 }}
                      >
                        営巣報告ステータス
                      </div>
                    </Grid>
                    <Grid
                      item
                      className={classes.tagDropdown}
                      xs={12}
                      md={12}
                      lg={8}
                    >
                        <div className={classes.tagWrapper}>
                          <Autocomplete
                            id="tag-select-autocomplete"
                            options={birdNestReportOption}
                            getOptionLabel={(option) => option.label || ""}
                            getOptionSelected={(option, value) =>
                              option.value.toString() === value.toString()
                            }
                            value={
                              selectedReportStatus.report_status !== undefined
                                ? birdNestReportOption.find(
                                    (status) =>
                                      status.value.toString() ===
                                      selectedReportStatus.report_status.toString(),
                                  ) || null
                                : null
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                const selectedValues = newValue;
                                handleSelectReportStatusChange(selectedValues);
                              } else {
                                handleSelectReportStatusChange({});
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder={
                                  selectedReportStatus.report_status !== undefined
                                    ? ""
                                    : "選択する"
                                }
                                className={classes.tagSelect}
                              />
                            )}
                            renderOption={(option, { selected }) => {
                              return (
                                <Box
                                  width="100%"
                                  display="flex"
                                  flexDirection="column"
                                >
                                  <Box
                                    pl={option.indent ? 3 : 0}
                                  >
                                    <Typography>
                                      {option.label}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={flagRemovedChecked}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setFlagRemovedChecked(checked);
                                  if (selectedReportStatus.report_status) {
                                    setSelectedReportStatus({
                                      ...selectedReportStatus,
                                      flag_removed: checked,
                                    });
                                  }
                                }}
                                color="default"
                                inputProps={{ "aria-label": "営巣撤去済み" }}
                              />
                            }
                            label="営巣撤去済み"
                            style={{ marginLeft: 8, paddingRight: 0 }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            color="default"
                            className={classes.saveButton}
                            onClick={setSavedSelectedReportStatusData}
                            disabled={!selectedReportStatus.report_status}
                            style={{ marginLeft: 6, marginTop: 6 }}
                          >
                            {reportStatusSavingLoader ? "保存中.." : "保存"}
                          </Button>
                        </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </div>
  );
};

ReportMeta.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  selectedReportTag: state.app.selectedReportTag,
  selectedReportStatus: state.app.selectedReportStatus,
  tagSavingLoader: state.app.tagSavingLoader,
  reportStatusSavingLoader: state.app.reportStatusSavingLoader,
});

const mapDispatchToProps = (dispatch) => ({
  setSelectedReportTag: (param) => dispatch(setSelectedReportTag(param)),
  setSavedSelectedReportTagData: (param) => dispatch(setSavedSelectedReportTagData(param)),
  setSelectedReportStatus: (param) => dispatch(setSelectedReportStatus(param)),
  setSavedSelectedReportStatusData: (param) => dispatch(setSavedSelectedReportStatusData(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(null, { withTheme: true })(ReportMeta));
